import type {
  Chart as ChartJs,
  ChartConfiguration,
  ChartDataset,
  ChartEvent,
  TooltipModel,
} from "chart.js";

import type {
  Chart,
  Curve,
  Dash,
  DateUnit,
  Magnitude,
  Series,
  SeriesColor,
  Stroke,
  ValueFormat,
  XAxis,
} from "~/lib/chart-spec";

export interface ChartTheme {
  colors: Record<SeriesColor, string>;
  fills: Record<SeriesColor, string>;
  surface: string;
  grid: string;
  ink: string;
  inkStrong: string;
  fontFamily: string;
}

export type ChartKind = "bar" | "line";

export type TooltipRenderer = (context: {
  chart: ChartJs<ChartKind>;
  tooltip: TooltipModel<ChartKind>;
}) => void;

type Config = ChartConfiguration<ChartKind, (number | null)[], string>;
type Dataset = ChartDataset<ChartKind, (number | null)[]>;

// Theme color steps per slot. Tailwind only includes a theme variable when a
// utility uses it, so css/safelist.css adds utilities for these steps.
const SLOTS: Record<SeriesColor, { color: string; fill: string }> = {
  blue: { color: "blue-500", fill: "blue-50" },
  orange: { color: "orange-500", fill: "orange-50" },
  green: { color: "green-500", fill: "green-50" },
  red: { color: "red-500", fill: "red-50" },
  purple: { color: "purple-400", fill: "purple-50" },
  yellow: { color: "yellow-400", fill: "yellow-50" },
  pink: { color: "pink-200", fill: "pink-50" },
  gray: { color: "gray-400", fill: "gray-100" },
  light_purple: { color: "purple-200", fill: "purple-50" },
  light_green: { color: "green-200", fill: "green-50" },
  light_gray: { color: "gray-300", fill: "gray-100" },
};

const PALETTE: SeriesColor[] = [
  "blue",
  "orange",
  "green",
  "red",
  "purple",
  "yellow",
  "pink",
  "gray",
];

// Matches the number format of `c.values.money` on the server.
const LOCALE = "en-US";

type FixedMagnitude = Exclude<Magnitude, "auto">;

const MAGNITUDES: Record<FixedMagnitude, { divisor: number; prefix: string }> =
  {
    one: { divisor: 1, prefix: "" },
    thousand: { divisor: 1e3, prefix: "k" },
    million: { divisor: 1e6, prefix: "M" },
    billion: { divisor: 1e9, prefix: "B" },
  };

const STROKE_WIDTHS: Record<Stroke, number> = {
  thin: 1,
  regular: 2,
  thick: 3,
};

const DASHES: Record<Dash, number[]> = {
  solid: [],
  dashed: [6, 4],
  dotted: [1, 4],
};

const number = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });
const percent = new Intl.NumberFormat(LOCALE, {
  style: "percent",
  maximumFractionDigits: 1,
});

export const readTheme = (element: Element): ChartTheme => {
  const style = getComputedStyle(element);
  const token = (step: string): string =>
    style.getPropertyValue(`--color-${step}`).trim();
  const slots = Object.entries(SLOTS) as [
    SeriesColor,
    { color: string; fill: string },
  ][];
  return {
    colors: Object.fromEntries(
      slots.map(([slot, { color }]) => [slot, token(color)]),
    ) as Record<SeriesColor, string>,
    fills: Object.fromEntries(
      slots.map(([slot, { fill }]) => [slot, token(fill)]),
    ) as Record<SeriesColor, string>,
    surface: token("white"),
    grid: token("gray-200"),
    ink: token("gray-500"),
    inkStrong: token("gray-900"),
    fontFamily: style.fontFamily,
  };
};

export const tickMagnitude = (
  format: ValueFormat,
  ticks: number[],
): FixedMagnitude => {
  if (format.kind === "percent") return "one";
  if (format.magnitude !== "auto") return format.magnitude;
  const highest = Math.max(0, ...ticks.map(Math.abs));
  const candidates: FixedMagnitude[] = ["billion", "million", "thousand"];
  return (
    candidates.find((magnitude) => highest >= MAGNITUDES[magnitude].divisor) ??
    "one"
  );
};

export const formatValue = (
  value: number,
  format: ValueFormat,
  magnitude: FixedMagnitude,
): string => {
  const { divisor, prefix } = MAGNITUDES[magnitude];
  switch (format.kind) {
    case "percent":
      return percent.format(value);
    case "number":
      return `${number.format(value / divisor)}${prefix}`;
    case "money":
      return `${number.format(value / divisor)} ${prefix}${format.currency}`;
  }
};

export const formatDate = (iso: string, unit: DateUnit): string => {
  const date = new Date(iso);
  switch (unit) {
    case "day":
      return date.toLocaleDateString(LOCALE, {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
      });
    case "month":
      return date.toLocaleDateString(LOCALE, {
        timeZone: "UTC",
        month: "short",
        year: "numeric",
      });
    case "quarter":
      return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
    case "year":
      return String(date.getUTCFullYear());
  }
};

const xLabels = (x: XAxis): string[] => {
  switch (x.kind) {
    case "category":
      return x.labels;
    case "date":
      return x.dates.map((date) => formatDate(date, x.unit));
  }
};

const stepped = (curve: Curve): false | "before" | "after" | "middle" => {
  switch (curve) {
    case "linear":
    case "smooth":
      return false;
    case "step_before":
      return "before";
    case "step_after":
      return "after";
    case "step_middle":
      return "middle";
  }
};

export const seriesColor = (series: Series, index: number): SeriesColor =>
  series.color ?? PALETTE[index % PALETTE.length] ?? "blue";

const dataset = (series: Series, index: number, theme: ChartTheme): Dataset => {
  const color = theme.colors[seriesColor(series, index)];
  switch (series.kind) {
    case "bar":
      return {
        type: "bar",
        label: series.label,
        data: series.values,
        // Bars without a stack get a stack of their own, so they are drawn side by side.
        stack: series.stack ?? `bar:${index}`,
        order: 1,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderRadius: 4,
        borderSkipped: "start",
        maxBarThickness: 64,
      };
    case "line":
      return {
        type: "line",
        label: series.label,
        data: series.values,
        // A stack of its own, so the line is not stacked on the bars or other lines.
        stack: `line:${index}`,
        // Lower orders are drawn on top, so filled areas are drawn below bars and lines.
        order: series.is_filled ? 2 : 0,
        borderColor: color,
        backgroundColor: series.is_filled
          ? theme.fills[seriesColor(series, index)]
          : color,
        fill: series.is_filled ? "origin" : false,
        borderWidth: STROKE_WIDTHS[series.stroke],
        borderDash: DASHES[series.dash],
        borderCapStyle: "round",
        borderJoinStyle: "round",
        tension: 0,
        cubicInterpolationMode:
          series.curve === "smooth" ? "monotone" : "default",
        stepped: stepped(series.curve),
        pointRadius: series.show_points ? 4 : 0,
        pointHoverRadius: 4,
        pointBackgroundColor: color,
        pointBorderColor: theme.surface,
        pointBorderWidth: 2,
        pointHoverBorderWidth: 2,
      };
  }
};

/** `url` with the query parameters of `from` added. */
const withQuery = (url: string, from: string): string => {
  const merged = new URL(url, window.location.href);
  new URL(from, window.location.href).searchParams.forEach((value, name) => {
    merged.searchParams.set(name, value);
  });
  return merged.href;
};

export const buildConfig = (
  spec: Chart,
  theme: ChartTheme,
  renderTooltip: TooltipRenderer,
  navigate: (url: string) => void = (url) => {
    window.location.assign(url);
  },
): Config => {
  const format = spec.y.format;
  const hasBars = spec.series.some((series) => series.kind === "bar");
  const labelAngle = spec.x.label_angle;
  const tickUrls = spec.x.urls;
  const urlAt = (event: ChartEvent, chart: ChartJs): string | null => {
    if (event.native == null || event.x == null || event.y == null) return null;
    const xScale = chart.scales["x"];
    if (
      xScale &&
      event.y > chart.chartArea.bottom &&
      event.y <= xScale.bottom
    ) {
      return (
        tickUrls?.[Math.round(xScale.getValueForPixel(event.x) ?? -1)] ?? null
      );
    }
    const [element] = chart.getElementsAtEventForMode(
      event.native,
      "nearest",
      { intersect: true },
      false,
    );
    if (!element) return null;
    const seriesUrl = spec.series[element.datasetIndex]?.url ?? null;
    if (!tickUrls) return seriesUrl;
    const tickUrl = tickUrls[element.index] ?? null;
    return seriesUrl && tickUrl ? withQuery(seriesUrl, tickUrl) : null;
  };
  const font = { family: theme.fontFamily, size: 12 };

  return {
    type: hasBars ? "bar" : "line",
    plugins: [
      {
        // onHover and onClick only get events in the chart area, not on the
        // tick labels.
        id: "urls",
        afterEvent: (chart, { event }) => {
          const url = urlAt(event, chart);
          chart.canvas.style.cursor = url ? "pointer" : "";
          if (url && event.type === "click") navigate(url);
        },
      },
    ],
    data: {
      labels: xLabels(spec.x),
      datasets: spec.series.map((series, index) =>
        dataset(series, index, theme),
      ),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      font,
      layout: { padding: spec.is_compact ? 2 : 0 },
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          type: "category",
          display: !spec.is_compact,
          stacked: true,
          offset: hasBars,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: theme.ink,
            font,
            minRotation: labelAngle,
            maxRotation: labelAngle,
            autoSkipPadding: 8,
          },
        },
        y: {
          type: "linear",
          display: !spec.is_compact,
          stacked: true,
          beginAtZero: true,
          grid: { color: theme.grid, drawTicks: false },
          border: { display: false },
          ticks: {
            color: theme.ink,
            font,
            padding: 8,
            maxTicksLimit: 6,
            callback: (value, _index, ticks) =>
              Number(value) === 0
                ? "0"
                : formatValue(
                    Number(value),
                    format,
                    tickMagnitude(
                      format,
                      ticks.map((tick) => tick.value),
                    ),
                  ),
          },
        },
      },
      plugins: {
        // The legend and the tooltip are HTML next to the canvas. See chart-dom.ts.
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: renderTooltip,
          filter: (item) =>
            item.raw !== null &&
            !(spec.series[item.datasetIndex]?.kind === "bar" && item.raw === 0),
          // Bars are listed in reverse series order, so the top segment of the stack is
          // first. Lines are listed in series order.
          itemSort: (a, b) =>
            spec.series[a.datasetIndex]?.kind === "bar" &&
            spec.series[b.datasetIndex]?.kind === "bar"
              ? b.datasetIndex - a.datasetIndex
              : a.datasetIndex - b.datasetIndex,
        },
      },
    },
  };
};
